import { z } from 'zod';
import { OffsetPaginationParamsSchema } from '../offset-pagination.js';
// ---------------------------------------------------------------------------
// Virtual basket, destruction protocols and stock corrections (domain 5,
// REQ-KIT-030…033; issue #27 atom 4).
//
// Q-KIT-011: one table kit_out; a basket row is one whose kitOutProtocolId is
// null. D-C: totalStock and a batch quantity may be NEGATIVE (the stock formula
// is income − usage − take-offs + corrections), so those schemas are plain
// integers. A correction numberOfKits is SIGNED and zero is rejected.
// ---------------------------------------------------------------------------
// --- Requests --------------------------------------------------------------
export const AddKitOutRequestSchema = z.object({
    labKitId: z.number().int().positive({ message: 'Wybór typu zestawu jest wymagany' }),
    labKitExpirationId: z.number().int().positive().nullable().optional(),
    numberOfKits: z
        .number({ message: 'Liczba kitów musi być liczbą całkowitą' })
        .int()
        .min(1, { message: 'Liczba odłożonych kitów musi być większa od zera' }),
    outDate: z.iso.date({ message: 'Data usunięcia ze stanu jest wymagana' }),
    comments: z.string().max(10_000).optional(),
});
export const CreateKitOutProtocolRequestSchema = z.object({
    trialLaboratoryId: z.number().int().positive({ message: 'Wybór laboratorium jest wymagany' }),
    protocolDate: z.iso.date({ message: 'Data zniszczenia jest wymagana' }),
    comments: z.string().max(10_000).optional(),
    basketItemIds: z
        .array(z.number().int().positive())
        .min(1, { message: 'Protokół zniszczenia wymaga co najmniej jednej pozycji z kosza' }),
});
export const CreateKitCorrectionRequestSchema = z.object({
    labKitId: z.number().int().positive({ message: 'Wybór typu zestawu jest wymagany' }),
    labKitExpirationId: z.number().int().positive().nullable().optional(),
    numberOfKits: z
        .number({ message: 'Liczba kitów musi być liczbą całkowitą' })
        .int()
        .refine((value) => value !== 0, {
        message: 'Korekta musi być liczbą różną od zera (dodatnia zwiększa stan, ujemna obniża)',
    }),
    correctionDate: z.iso.date({ message: 'Data korekty jest wymagana' }),
    comments: z.string().max(10_000).optional(),
});
// --- Queries ---------------------------------------------------------------
export const KitBasketQuerySchema = z.object({
    /** Narrow the basket to one trial (optional). */
    trialId: z.coerce.number().int().positive().optional(),
});
export const KitOutProtocolListQuerySchema = OffsetPaginationParamsSchema.extend({
    /** Narrow the protocol register to one laboratory of a trial (optional). */
    trialLaboratoryId: z.coerce.number().int().positive().optional(),
});
export const KitCorrectionListQuerySchema = OffsetPaginationParamsSchema.extend({
    /** Narrow the corrections register to one trial (optional). */
    trialId: z.coerce.number().int().positive().optional(),
    /** Narrow the corrections register to one kit (optional). */
    labKitId: z.coerce.number().int().positive().optional(),
});
// --- DTOs ------------------------------------------------------------------
export const KitOutDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    labKitId: z.number().int().positive(),
    labKitExpirationId: z.number().int().positive().nullable(),
    /** null = still in the virtual basket; set = permanently destroyed via a protocol. */
    kitOutProtocolId: z.number().int().positive().nullable(),
    numberOfKits: z.number().int().positive(),
    outDate: z.iso.date(),
    comments: z.string().nullable(),
    createdByUserId: z.number().int().positive().nullable(),
    createdAt: z.iso.datetime(),
});
export const KitBasketEntryDtoSchema = KitOutDtoSchema.extend({
    /** Enriched display fields for the basket table (optional). */
    kitName: z.string().optional(),
    laboratoryName: z.string().optional(),
    trialId: z.number().int().positive().optional(),
});
export const KitOutProtocolDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialLaboratoryId: z.number().int().positive(),
    protocolDate: z.iso.date(),
    comments: z.string().nullable(),
    createdByUserId: z.number().int().positive().nullable(),
    createdAt: z.iso.datetime(),
});
export const KitOutProtocolWithItemsDtoSchema = KitOutProtocolDtoSchema.extend({
    items: z.array(KitOutDtoSchema),
});
export const KitCorrectionDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    labKitId: z.number().int().positive(),
    labKitExpirationId: z.number().int().positive().nullable(),
    /** Signed: positive raises the stock, negative lowers it; never zero (REQ-KIT-033). */
    numberOfKits: z.number().int().refine((value) => value !== 0, {
        message: 'Korekta musi być liczbą różną od zera',
    }),
    correctionDate: z.iso.date(),
    comments: z.string().nullable(),
    createdByUserId: z.number().int().positive().nullable(),
    createdAt: z.iso.datetime(),
    /** Enriched display fields for the corrections register (optional). */
    kitName: z.string().optional(),
    laboratoryName: z.string().optional(),
    trialId: z.number().int().positive().optional(),
});
//# sourceMappingURL=kit-waste.schema.js.map